import type { createApp } from 'vue';
import EventBus from './eventbus';
import { CommitSha, DomainConfig, IsDev, IsLive, LogRocketCode } from './env';
import { Constants, GlobalSettings, LanguageUtil } from '@fibl/oxs-common';
import { useGlobalStore } from '@/stores/global';
import { ElMessage } from 'element-plus';
import { Request } from '@fibl/zdb-common';
import { DOMAIN } from './env';
import { log, post } from './request';
import { config } from 'md-editor-v3';
import screenfull from 'screenfull';
import cropper from 'cropperjs';
import * as SearchRepo from '../repository/search.js';
import { useListSettingsStore } from '@/stores/listsettings';
import { showErrorMessage } from './error';

export type WebConf = { prefix: string; website: Constants.WebConstant; country?: string };

const maintenanceError = 'service is currently not available. check back later';

function checkForMaintenance(e: any) {
	if (typeof e !== 'object') return false;
	if (!e || !('message' in e)) return false;
	if (e.message.includes(maintenanceError)) {
		useGlobalStore().maintenance = true;
		return true;
	}
	return false;
}

export async function boot(app: ReturnType<typeof createApp>, webConf: WebConf) {
	const favicon = document.getElementById('favicon') as HTMLLinkElement;
	if (getWebsiteAndCountry().website === Constants.WEB_RDB) {
		favicon.href = webConf.prefix + 'favicon-rdb.ico';
	} else {
		favicon.href = webConf.prefix + 'favicon-oxs.ico';
	}
	EventBus.on('account-switch', () => {
		globalStore.relatedStore.reset();
		settingStore.reset();
		globalStore.offerSearchStore.resetDynFilters();
		globalStore.offerSearchStore.filterDefault.menu = null;
		globalStore.messagingStore.tableFilter.context = undefined;
		globalStore.websiteStore.setUiLocale(globalStore.tokenStore.main.details.userData.uiLocale  as LanguageUtil.LocaleCode);
		globalStore.mailerStore.userAutoVerification();
		if (globalStore.tokenStore.role !== 'agr')
			settingStore.set({ list: 'request-list', key: 'showOnlyFillable', value: true });
		if (!globalStore.tokenStore.insRemoveAccessToMessage && globalStore.mailerStore.needsEmailVerification && !globalStore.mailerStore.isVerficationDialogShown) {
			globalStore.mailerStore.showVerificationDialog = true;
		}
	});
	app.config.errorHandler = function (error: any, vm, info) {
		console.error('vue.errorHandler', error, vm, info);
		let maintenance = checkForMaintenance(error);
		if (!maintenance) {
			const msg = getErrorMessage(error);
			EventBus.emit('error', msg);
			showErrorMessage(error, msg);
		}
	};
	app.config.warnHandler = function (error, vm, info) {
		checkForMaintenance(error);
		console.warn('vue.warnHandler', error, vm, info);
	};
	window.addEventListener('unhandledrejection', (event) => {
		console.log('unhandledRejection', event);
		if (!event?.reason) return;
		checkForMaintenance(event.reason);
		EventBus.emit('error', event.reason.message);
	});
	window.onerror = function (message, source, lineno, colno, error) {
		console.log('window.onerror', message, source, lineno, colno, error);
		let maintenance = checkForMaintenance(error);
		if (!maintenance) {
			// logError({ message, source, lineno, colno, error });
			EventBus.emit('error', `${message} / ${source} / ${lineno} / ${error?.stack}`);
		}
	};

	Request.setRequestDefaults({
		domain: DOMAIN,
	});
	Request.initCache({
		ttl: 1000 * 60 * 60,
		ttlAutopurge: false,
		max: 2000,
	});
	Request.setHandler((p) => {
		return post(p.url, p.payload, p.auth);
	});

	config({
		editorExtensions: {
			screenfull: {
				instance: screenfull,
			},
			cropper: {
				instance: cropper,
			},
		},
	});
	const globalStore = useGlobalStore();
	const settingStore = useListSettingsStore();
	globalStore.websiteStore.initSite(webConf.website, (webConf.country || 'DE') as any);
	if (LogRocketCode) {
		import('logrocket').then(LogRocket => {
			LogRocket.default.init(LogRocketCode);
		});
	}

	if (webConf.website !== Constants.WEB_OXS_SELECT) {
		try {
			await globalStore.tokenStore.initApp();
		} catch (error: any) {
			const maintenance = checkForMaintenance(error);
			if (maintenance) return;
			logError({ error: getErrorMessage(error) })
			globalStore.tokenStore.forgetTokens();
			localStorage.clear();
			sessionStorage.clear();
			if (!document.location.search.includes('coldBoot')) {
				if (document.location.search) {
					document.location.search += '&coldBoot=1'
				} else {
					document.location.search = 'coldBoot=1'
				}
			}
		}
		moreInit();	//run split off
	}
}

async function moreInit() {
	const globalStore = useGlobalStore();
	let ent = await SearchRepo.getEntityByAlias<GlobalSettings.Settings>(
		'global-settings',
		'global-settings',
	);
	globalStore.websiteStore.initSettings(ent);
	await globalStore.messagingStore.getUnreadMessageCount();
}

export function getWebsiteAndCountry(): WebConf {
	let url = new URL(document.location.href);
	let norm = url.hostname + url.pathname;
	for (let [prefix, value] of Object.entries(DomainConfig)) {
		let u = new URL(`http://${prefix}`);
		let cmp = u.hostname + u.pathname;
		// console.log('checking', norm, cmp)
		if (norm.startsWith(cmp)) {
			let p = value.split(':');
			return {
				prefix: u.pathname,
				website: p[0] as Constants.WebConstant,
				country: p[1],
			};
		}
	}
	return {
		prefix: '/',
		website: Constants.WEB_OXS_SELECT,
	};
}

export function getErrorMessage(err: any) {
	if ('servicestack' in err) {
		let msg = '';
		for (let row of err.servicestack) {
			if (row.service) {
				msg += row.service + ': ';
			}
			if (row.message) {
				msg += row.message + '\n';
			}
			if (row.details) {
				msg += '(' + row.details + ')\n';
			}
		}
		return msg;
	}
	if ('message' in err) {
		return err.message;
	}
	return JSON.stringify(err);
}

const loggedErrors = new Set<string>();
async function logError(data: any) {
	try {
		const line = JSON.stringify(data);
		if (loggedErrors.has(line)) return;
		loggedErrors.add(line);
		await log({ ...data, ...getExtraLogData() });
	} catch (e) {
		console.log('error during error logging', e);
		console.log('tried to log this error: ', data);
	}
}

function getExtraLogData() {
	return {
		ua: navigator.userAgent,
		lang: navigator.language,
		plat: navigator.platform,
		loc: window.location.href,
		sx: screen.width,
		sy: screen.height,
		app: 'oxs',
		build: CommitSha,
		stack: new Error('trace').stack
	};
}